import axios from 'axios';
import { pageContext } from './page_context';

/**
 * Fetch the current server time from our express server
 * @param {Function} callback - a callback to return the server time once the promise has resovled
 * @returns {Promise} - the current unix server time
 */
export const fetchServerTime = (callback) => {
    return axios(`/${pageContext.getVertical()}/journey/server_time`)
        .then((response) => callback(response.data))
        // eslint-disable-next-line no-console
        .catch((err) => console.error(err));
};

/**
 * Converts a date string to unix epoch time
 * @param {String} dateString - the date string to convert to unix time
 * @returns {number} - the unix time converted from a date string
 */
export const convertDateStringToEpoch = (dateString) => {
    return new Date(dateString).getTime() / 1000;
};

/**
 * Converts a unix based epoch number to UTC date string
 * @param {Number} unix - unix time stamp to convert to utc date string
 * @returns {String} - the UTC date string converted from the unix time
 */
export const convertEpochToUTCDateString = (unix) => {
    const date = new Date(unix * 1000);
    return date.toUTCString();
};

/**
 * Converts a unix based epoch number to local date string
 * @param {Number} unix - unix time stamp to convert to local date string
 * @returns {String} - the local date string converted from the unix time
 */
export const convertEpochToLocalDateString = (unix) => {
    const date = new Date(unix * 1000);
    return date.toString();
};

/**
 * Validate the number of days in a given month, including leap years
 * @param {Number} m - month
 * @param {Number} y - year
 * @returns {Number} returns the number of days in a given month based on the year
 */
const daysInMonth = (m, y) => { // m is 0 indexed: 0-11
    switch (m) {
        case 2:
            return (y % 4 === 0 && y % 100) || y % 400 === 0 ? 29 : 28;
        case 9:
        case 4:
        case 6:
        case 11:
            return 30;
        default:
            return 31;
    }
};

/**
 * A small util function to evaluate a valid date
 * @param {String} d - day
 * @param {String} m - month
 * @param {String} y - year
 * @returns {Boolean} whether or not the given d, m, y of a date is valid
 */
const _isValidDate = (d, m, y) => {
    const radix = 10;
    return (
        parseInt(m, radix) > 0
        && parseInt(m, radix) < 13
        && parseInt(d, radix) > 0
        && parseInt(d, radix) <= daysInMonth(parseInt(m, radix), parseInt(y, radix))
    );
};
/**
 * Validate a date string e.g. 2017-01-01
 * @param {String} dateString The string to validate
 * @returns {Boolean} True if valid, false if invalid.
 */
export const isValidDateString = (dateString) => {
    // if no date has been supplied
    if (!dateString) {
        return false;
    }

    if (dateString instanceof Date) {
        return false;
    }

    // if a year month or day wasn't specified
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) {
        return false;
    }

    // check for correct '0', zero padding with days or months with a single digit
    if ((parseInt(month, 10) < 10 && !(month.indexOf('0') >= 0)) || (parseInt(day, 10) < 10 && !(day.indexOf('0') >= 0))) {
        return false;
    }

    // check if the number of days in the given month is valid, depending on the year (includes leap years)
    if (!_isValidDate(day, month, year)) {
        return false;
    }
    // parse the date and check edge case
    const normalizedDate = new Date(Date.parse(dateString));
    return !isNaN(normalizedDate);
};

/**
 * Add zeros to num until it has length === len
 * @param {Integer} num - The number to zero pad
 * @param {Integer} len - The required length of the zero padded number string
 * @returns {String} - num with the correct number of zeroes padding it
 */
export const pad = (num, len) => {
    // todo temp hack to stop an error showing in client side.
    if (num === null || !isFinite(num)) {
        return num;
    }

    const repeatAmount = len - num.toString().length;

    if (!isFinite(repeatAmount) || repeatAmount < 0) {
        return num;
    }

    return '0'.repeat(repeatAmount) + num;
};

/**
 * Determine if the daylight saving is observed which is limited to Australia time zone only.
 * @returns {boolean} - true if daylight saving is observed, false otherwise
 */
export const isDstObserved = () => {
    // Time difference from GMT/UTC in Jan & Jul
    const jan = new Date(new Date().getFullYear(), 0, 1);
    const jul = new Date(new Date().getFullYear(), 6, 1);
    // Daylight saving time is in between first Sunday in October and first Sunday in April
    const stdTimezoneOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    // Returns true when DST is observed in Southern Hemisphere
    return new Date().getTimezoneOffset() < stdTimezoneOffset;
};
